import type { FC, ReactNode } from "react";

import { EmailOtpCard } from "../auth/EmailOtpCard";
import {
  ArrowRightIcon,
  Chip,
  Eyebrow,
  LandingSection,
  MONO_LABEL_CLASS_NAME,
  Panel,
  PRIMARY_BUTTON_CLASS_NAME,
  SECONDARY_BUTTON_CLASS_NAME,
  SectionHeading,
  SubduedText,
  WindowFrame,
} from "./landingPrimitives";

interface PublicBillingSectionsProps {
  onRequestOtp: (email: string) => Promise<void>;
  onVerifyOtp: (
    email: string,
    code: string,
    rememberDevice: boolean,
  ) => Promise<void>;
}

interface BillingFaqData {
  answer: string;
  question: string;
}

interface PricingFeature {
  included: boolean;
  label: string;
}

interface PricingTier {
  cta: string;
  ctaHref: string;
  description: string;
  features: readonly PricingFeature[];
  highlight: boolean;
  name: string;
  price: string;
  priceNote: string;
}

const PRICING_TIERS: readonly PricingTier[] = [
  {
    cta: "Request access",
    ctaHref: "#register",
    description: "For teams getting started with AI-assisted development",
    features: [
      { included: true, label: "Full Jira + GitHub integration" },
      { included: true, label: "Parallel agent execution" },
      { included: true, label: "Codebase indexing" },
      { included: true, label: "QA mandate enforcement" },
      { included: true, label: "Multi-model orchestration" },
      { included: true, label: "Email support" },
      { included: false, label: "Priority execution queue" },
      { included: false, label: "Custom QA configuration" },
      { included: false, label: "Dedicated onboarding" },
    ],
    highlight: false,
    name: "Team",
    price: "$750",
    priceNote: "per Jira project / month",
  },
  {
    cta: "Contact us",
    ctaHref: "mailto:hello@artifaxtech.com?subject=Arti Enterprise",
    description:
      "For organizations scaling AI development across multiple teams",
    features: [
      { included: true, label: "Everything in Team" },
      { included: true, label: "Unlimited tasks" },
      { included: true, label: "Multi-repo, multi-team orchestration" },
      { included: true, label: "Priority execution queue" },
      { included: true, label: "Custom QA mandate configuration" },
      { included: true, label: "Dedicated support + onboarding" },
      { included: true, label: "SSO and access controls" },
      { included: true, label: "SLA guarantees" },
      { included: true, label: "Custom integrations" },
    ],
    highlight: true,
    name: "Enterprise",
    price: "Let's talk",
    priceNote: "custom pricing",
  },
] as const;

const BILLING_FAQ: readonly BillingFaqData[] = [
  {
    answer:
      "Arti handles implementation tasks that can be clearly described in a Jira ticket: feature scaffolding, CRUD endpoints, bug fixes, test writing, refactors, and more. It works best when the requirements are specific and the codebase is well-structured. Complex architectural decisions still benefit from human judgment.",
    question: "What types of tasks can Arti handle?",
  },
  {
    answer:
      "Arti accesses your repositories through authenticated GitHub integrations scoped to the repositories you explicitly grant. Code is processed in isolated execution environments and never stored beyond the task lifecycle. All access is audited and visible in your dashboard.",
    question: "How does Arti handle security and code access?",
  },
  {
    answer:
      "Yes. During onboarding, you select exactly which repositories Arti can access. You can add or revoke repository access at any time through the dashboard. Arti never accesses repositories outside your explicit configuration.",
    question: "Can I control which repositories Arti accesses?",
  },
  {
    answer:
      "Arti creates PRs against your existing branches and repositories. Your CI/CD pipeline runs on those PRs exactly as it would for any team member's code. Arti doesn't bypass or modify your pipeline; it works within it.",
    question: "How does Arti work with our existing CI/CD pipeline?",
  },
  {
    answer:
      "Every PR goes through your normal review process. Arti enforces QA mandates (tests must pass, type checks must succeed) before submitting, but the final merge decision is always yours. If output quality doesn't meet your bar, reject the PR like you would for any contributor.",
    question: "What happens if Arti gets something wrong?",
  },
] as const;

/** One line of a mono ledger: label, working, amount. */
const LedgerRow: FC<{ amount: ReactNode; label: string; working: string }> = ({
  amount,
  label,
  working,
}) => (
  <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 px-5 py-3.5">
    <span className="min-w-0">
      <span className={`block ${MONO_LABEL_CLASS_NAME} text-slate-300`}>
        {label}
      </span>
      <span className="mt-1 block font-mono text-[11px] tabular-nums text-slate-500">
        {working}
      </span>
    </span>
    <span className="font-mono text-base font-bold tabular-nums">{amount}</span>
  </div>
);

/* ------------------------------------------------------------------ */
/*  1. Hero                                                            */
/* ------------------------------------------------------------------ */

const INVOICE_LINES = [
  { amount: "$750", label: "Jira project", working: "Team plan · 1 × $750" },
  { amount: "$0", label: "Seats", working: "Unlimited teammates" },
  { amount: "$0", label: "AI compute", working: "Included in the plan" },
  { amount: "$0", label: "Token markup", working: "None" },
] as const;

const InvoicePreview: FC = () => (
  <WindowFrame
    title="Invoice preview · Team plan"
    status={<Chip tone="slate">Monthly</Chip>}
    corners
    footer={
      <div className="flex items-baseline justify-between gap-4">
        <span className={`${MONO_LABEL_CLASS_NAME} text-slate-400`}>
          Total per month
        </span>
        <span className="font-mono text-xl font-bold tabular-nums text-emerald-300">
          $750
        </span>
      </div>
    }
  >
    <div className="divide-y divide-slate-800">
      {INVOICE_LINES.map((line) => (
        <LedgerRow
          key={line.label}
          label={line.label}
          working={line.working}
          amount={
            <span
              className={
                line.amount === "$0" ? "text-slate-500" : "text-slate-100"
              }
            >
              {line.amount}
            </span>
          }
        />
      ))}
    </div>
  </WindowFrame>
);

export const PublicBillingHeroSection: FC = () => (
  <section className="px-4 pb-20 pt-16 sm:px-6 sm:pt-24 lg:pb-28">
    <div className="mx-auto grid max-w-7xl gap-14 lg:grid-cols-[1fr_1fr] lg:items-center">
      <div className="min-w-0">
        <Eyebrow>Pricing</Eyebrow>
        <h1 className="mt-5 text-balance font-display text-[2.6rem] font-bold normal-case leading-[1.04] !tracking-tight text-white sm:text-6xl">
          Pay for capacity. <SubduedText>Not seats.</SubduedText>
        </h1>
        <p className="mt-6 max-w-xl text-base leading-7 text-slate-400 sm:text-lg sm:leading-8">
          One flat price per Jira project with AI compute included. Add as many
          teammates as you like, and stop any month.
        </p>
        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          <a href="#pricing" className={PRIMARY_BUTTON_CLASS_NAME}>
            See plans
            <ArrowRightIcon />
          </a>
          <a href="/" className={SECONDARY_BUTTON_CLASS_NAME}>
            Back to overview
          </a>
        </div>
      </div>
      <div className="min-w-0">
        <InvoicePreview />
      </div>
    </div>
  </section>
);

/* ------------------------------------------------------------------ */
/*  2. Pricing tiers                                                   */
/* ------------------------------------------------------------------ */

const PricingFeatureList: FC<{ features: readonly PricingFeature[] }> = ({
  features,
}) => (
  <ul className="divide-y divide-slate-800 border-y border-slate-800">
    {features.map((feature) => (
      <li
        key={feature.label}
        className={`flex items-center gap-3 py-2.5 text-sm ${feature.included ? "text-slate-300" : "text-slate-600"}`}
      >
        {feature.included ? (
          <span
            className="h-1.5 w-1.5 flex-shrink-0 bg-emerald-400"
            aria-hidden="true"
          />
        ) : (
          <span
            className="h-px w-1.5 flex-shrink-0 bg-slate-600"
            aria-hidden="true"
          />
        )}
        <span className={feature.included ? "" : "line-through"}>
          {feature.label}
        </span>
      </li>
    ))}
  </ul>
);

const PricingCard: FC<{ tier: PricingTier }> = ({ tier }) => (
  <WindowFrame
    title={`${tier.name} plan`}
    status={tier.highlight ? <Chip square>Recommended</Chip> : null}
    corners={tier.highlight}
    className="flex flex-col"
  >
    <div className="flex flex-1 flex-col p-6 sm:p-8">
      <p className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="font-display text-5xl font-bold !tracking-tight text-white">
          {tier.price}
        </span>
        <span className={`${MONO_LABEL_CLASS_NAME} text-[10px] text-slate-500`}>
          {tier.priceNote}
        </span>
      </p>
      <p className="mt-4 text-sm leading-6 text-slate-400">
        {tier.description}
      </p>
      <div className="mt-8">
        <PricingFeatureList features={tier.features} />
      </div>
      <div className="mt-auto pt-8">
        <a
          href={tier.ctaHref}
          className={`w-full ${tier.highlight ? PRIMARY_BUTTON_CLASS_NAME : SECONDARY_BUTTON_CLASS_NAME}`}
        >
          {tier.cta}
          <ArrowRightIcon />
        </a>
      </div>
    </div>
  </WindowFrame>
);

export const PublicBillingPricingSection: FC = () => (
  <LandingSection id="pricing" band>
    <SectionHeading
      align="center"
      eyebrow="Plans"
      title="Choose your plan."
      body="Both plans include all AI compute, so model and API costs never show up as extra lines on your invoice."
    />
    <div className="mx-auto mt-14 grid max-w-5xl gap-6 lg:grid-cols-2">
      {PRICING_TIERS.map((tier) => (
        <PricingCard key={tier.name} tier={tier} />
      ))}
    </div>
  </LandingSection>
);

/* ------------------------------------------------------------------ */
/*  3. Terms                                                           */
/* ------------------------------------------------------------------ */

const TERMS = [
  {
    body: "Connect as many teammates as you need. You pay per project, not per person.",
    key: "Seats",
    title: "No per-seat pricing",
  },
  {
    body: "Scale up when you need more capacity and scale down when you don't.",
    key: "Contract",
    title: "Month to month",
  },
  {
    body: "Every agent run and what it cost shows up in your dashboard as it happens.",
    key: "Usage",
    title: "Full usage visibility",
  },
  {
    body: "There are no termination fees or lock-in periods, and your code stays in your repos.",
    key: "Exit",
    title: "Cancel anytime",
  },
] as const;

const TermRow: FC<{ term: (typeof TERMS)[number] }> = ({ term }) => (
  <div className="grid gap-2 px-5 py-4 sm:grid-cols-[7.5rem_1fr] sm:gap-6">
    <dt className={`${MONO_LABEL_CLASS_NAME} pt-0.5 text-emerald-400`}>
      {term.key}
    </dt>
    <dd>
      <span className="block text-sm font-semibold text-white">
        {term.title}
      </span>
      <span className="mt-1 block text-sm leading-6 text-slate-400">
        {term.body}
      </span>
    </dd>
  </div>
);

export const PublicBillingTransparencySection: FC = () => (
  <LandingSection>
    <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
      <SectionHeading
        eyebrow="Terms"
        title="Straightforward terms, full visibility."
        body="We want your invoice to be as easy to audit as the work itself."
      />
      <WindowFrame title="Terms" className="min-w-0">
        <dl className="divide-y divide-slate-800">
          {TERMS.map((term) => (
            <TermRow key={term.key} term={term} />
          ))}
        </dl>
      </WindowFrame>
    </div>
  </LandingSection>
);

/* ------------------------------------------------------------------ */
/*  4. FAQ                                                             */
/* ------------------------------------------------------------------ */

export const PublicBillingFaqSection: FC = () => (
  <LandingSection id="faq" band>
    <SectionHeading
      align="center"
      eyebrow="FAQ"
      title="What engineering leaders ask before adopting Arti."
    />
    <Panel className="mx-auto mt-14 max-w-4xl">
      <ol className="divide-y-2 divide-slate-800">
        {BILLING_FAQ.map((item, index) => (
          <li
            key={item.question}
            className="grid gap-3 p-6 sm:grid-cols-[3rem_1fr] sm:gap-4 sm:p-7"
          >
            <span className="font-mono text-xs font-bold tabular-nums tracking-[0.18em] text-emerald-400">
              Q{String(index + 1).padStart(2, "0")}
            </span>
            <div>
              <h3 className="font-display text-lg font-bold normal-case !tracking-tight text-white">
                {item.question}
              </h3>
              <p className="mt-3 text-sm leading-6 text-slate-400">
                {item.answer}
              </p>
            </div>
          </li>
        ))}
      </ol>
    </Panel>
  </LandingSection>
);

/* ------------------------------------------------------------------ */
/*  5. Register CTA                                                    */
/* ------------------------------------------------------------------ */

export const PublicBillingRegisterSection: FC<PublicBillingSectionsProps> = ({
  onRequestOtp,
  onVerifyOtp,
}) => (
  <section id="register" className="scroll-mt-24 px-4 pb-24 pt-8 sm:px-6">
    <Panel className="mx-auto max-w-7xl overflow-hidden">
      <div className="relative grid gap-10 p-6 sm:p-10 lg:grid-cols-2 lg:items-center lg:p-14">
        <div className="max-w-xl">
          <Eyebrow>Get started</Eyebrow>
          <h2 className="mt-5 text-balance font-display text-3xl font-bold normal-case leading-[1.1] !tracking-tight text-white sm:text-4xl lg:text-5xl">
            Join the teams <SubduedText>already testing Arti.</SubduedText>
          </h2>
          <p className="mt-5 text-base leading-7 text-slate-400 sm:text-lg sm:leading-8">
            Sign up with your work email. You connect your repos and Jira board
            during onboarding. Arti is in limited early access.
          </p>
        </div>
        <EmailOtpCard
          onRequestOtp={onRequestOtp}
          onVerifyOtp={onVerifyOtp}
          surface="landing"
        />
      </div>
    </Panel>
  </section>
);
